import React, { useEffect, useState } from "react";
import axios from "axios";
import Layout from "../../components/Layout/Layout";
import Sidebar from "../../components/Dashboard/Sidebar";
import { useAuth } from "../../context/auth";
import Swal from "sweetalert2";
import { FiTrash2, FiUserX, FiUserCheck, FiMail, FiUser } from "react-icons/fi";

const USERS_PER_PAGE = 50;

const Users = () => {
  const [auth] = useAuth();
  const [users, setUsers] = useState([]);
  // Removed sidebarOpen state
  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useState("");

  const getUsers = async () => {
    try {
      const { data } = await axios.get("/api/v1/auth/all-users");
      setUsers(data.users || []);
    } catch (error) {
      console.log(error);
      Swal.fire({
        title: "Error",
        text: "Failed to fetch users",
        icon: "error",
        background: "#1f2937",
        color: "#fff",
        confirmButtonColor: "#6b7280",
      });
    }
  };

  useEffect(() => {
    if (auth?.token) getUsers();
  }, [auth?.token]);

  const handleBan = async (id) => {
    const result = await Swal.fire({
      title: "Ban this user?",
      text: "They will not be able to log in.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#6b7280",
      confirmButtonText: "Ban",
      background: "#111827",
      color: "#fff",
    });

    if (result.isConfirmed) {
      try {
        await axios.post(`/api/v1/auth/ban/${id}`);
        Swal.fire({
          title: "Banned!",
          text: "User has been banned.",
          icon: "success",
          background: "#111827",
          color: "#fff",
        });
        getUsers();
      } catch {
        Swal.fire({
          title: "Error",
          text: "Failed to ban user",
          icon: "error",
          background: "#111827",
          color: "#fff",
        });
      }
    }
  };

  const handleUnban = async (id) => {
    const result = await Swal.fire({
      title: "Unban this user?",
      icon: "info",
      showCancelButton: true,
      confirmButtonColor: "#22c55e",
      cancelButtonColor: "#6b7280",
      confirmButtonText: "Unban",
      background: "#111827",
      color: "#fff",
    });

    if (result.isConfirmed) {
      try {
        await axios.post(`/api/v1/auth/unban/${id}`);
        Swal.fire({
          title: "Unbanned!",
          text: "User is active again.",
          icon: "success",
          background: "#111827",
          color: "#fff",
        });
        getUsers();
      } catch {
        Swal.fire({
          title: "Error",
          text: "Failed to unban user",
          icon: "error",
          background: "#111827",
          color: "#fff",
        });
      }
    }
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Delete this user?",
      text: "This action cannot be undone.",
      icon: "error",
      showCancelButton: true,
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#6b7280",
      confirmButtonText: "Delete",
      background: "#111827",
      color: "#fff",
    });

    if (result.isConfirmed) {
      try {
        await axios.delete(`/api/v1/auth/delete/${id}`);
        Swal.fire({
          title: "Deleted!",
          text: "User has been removed.",
          icon: "success",
          background: "#111827",
          color: "#fff",
        });
        getUsers();
      } catch {
        Swal.fire({
          title: "Error",
          text: "Failed to delete user",
          icon: "error",
          background: "#111827",
          color: "#fff",
        });
      }
    }
  };

  // Pagination & sorting
  const sortedUsers = [...users].sort((a, b) => {
    if (sortBy === "name") return a.name.localeCompare(b.name);
    if (sortBy === "email") return a.email.localeCompare(b.email);
    if (sortBy === "role") return b.role - a.role;
    return 0;
  });

  const totalPages = Math.ceil(users.length / USERS_PER_PAGE);
  const paginatedUsers = sortedUsers.slice(
    (currentPage - 1) * USERS_PER_PAGE,
    currentPage * USERS_PER_PAGE
  );

  return (
    <Layout title="Dashboard - All Users">
      <div className="min-h-screen bg-gray-50 mt-20">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Header */}
          <div className="mb-8 flex items-center justify-between">
            <div>
              <h1 className="text-3xl lg:text-4xl font-bold text-gray-900">
                All Users
              </h1>
              <p className="text-gray-600 mt-1.5">
                Manage all registered users
              </p>
            </div>
            {/* Menu Toggle Button Removed */}
          </div>

          <div className="flex flex-col lg:flex-row gap-6">
            {/* Sidebar */}
            <aside className="w-full lg:w-72 flex-shrink-0">
              <Sidebar
                auth={auth}
                activeTab="users"
                // setSidebarOpen={setSidebarOpen} // Removed
              />
            </aside>

            {/* Main Content */}
            <main className="flex-1 min-w-0">
              {/* Sort Dropdown */}
              <div className="mb-4 flex items-center gap-4">
                <label className="text-gray-700 font-medium">Sort by:</label>
                <select
                  className="border border-gray-300 rounded-md px-3 py-1 text-gray-700 focus:outline-none focus:ring focus:ring-gray-300"
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                >
                  <option value="">None</option>
                  <option value="name">Name</option>
                  <option value="email">Email</option>
                  <option value="role">Role</option>
                </select>
              </div>

              {/* Users Table */}
              <div className="bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 text-left">
                    <thead className="bg-gray-50">
                      <tr>
                        {["Name", "Status", "Role", "Ban/Unban", "Delete"].map(
                          (head) => (
                            <th
                              key={head}
                              className="px-6 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide"
                            >
                              {head}
                            </th>
                          )
                        )}
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {paginatedUsers.length === 0 ? (
                        <tr>
                          <td
                            colSpan="6"
                            className="px-6 py-10 text-center text-gray-500"
                          >
                            No users found
                          </td>
                        </tr>
                      ) : (
                        paginatedUsers.map((user) => (
                          <tr key={user._id} className="hover:bg-gray-50">
                            <td className="px-2 py-2 flex items-center gap-2 text-sm font-medium text-gray-900">
                              <FiUser className="text-gray-400" />
                              {user.name}
                            </td>
                            <td className="px-2 py-2 text-sm text-gray-700 flex items-center gap-2">
                              <FiMail className="text-gray-400" />
                              {user.email}
                            </td>
                            <td className="px-2 py-2">
                              <span
                                className={`px-3 py-1 text-xs font-medium rounded-full ${
                                  user.isBanned
                                    ? "bg-red-100 text-red-700"
                                    : "bg-green-100 text-green-700"
                                }`}
                              >
                                {user.isBanned ? "Banned" : "Active"}
                              </span>
                            </td>
                            <td className="px-2 py-2 text-sm text-gray-800">
                              {user.role === 1 ? "Admin" : "User"}
                            </td>
                            <td className="px-2 py-2">
                              {!user.isBanned ? (
                                <button
                                  onClick={() => handleBan(user._id)}
                                  className="flex items-center gap-1 text-gray-700 hover:text-red-600"
                                  title="Ban User"
                                >
                                  <FiUserX size={18} /> <span>Ban</span>
                                </button>
                              ) : (
                                <button
                                  onClick={() => handleUnban(user._id)}
                                  className="flex items-center gap-1 text-gray-700 hover:text-green-600"
                                  title="Unban User"
                                >
                                  <FiUserCheck size={18} /> <span>Unban</span>
                                </button>
                              )}
                            </td>
                            <td className="px-2 py-2">
                              <button
                                onClick={() => handleDelete(user._id)}
                                className="flex items-center gap-1 text-gray-700 hover:text-red-600"
                                title="Delete User"
                              >
                                <FiTrash2 size={18} /> <span>Delete</span>
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Rich Pagination */}
                <div className="px-4 py-3 bg-gray-50 flex flex-col sm:flex-row items-center justify-between border-t border-gray-200 gap-2 sm:gap-0">
                  <div className="flex gap-2">
                    <button
                      onClick={() => setCurrentPage(1)}
                      disabled={currentPage === 1}
                      className="px-3 py-1 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-100 disabled:opacity-50"
                    >
                      First
                    </button>
                    <button
                      onClick={() =>
                        setCurrentPage((prev) => Math.max(prev - 1, 1))
                      }
                      disabled={currentPage === 1}
                      className="px-3 py-1 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-100 disabled:opacity-50"
                    >
                      Prev
                    </button>
                  </div>
                  <div className="flex gap-1 overflow-x-auto">
                    {[...Array(totalPages).keys()].map((i) => (
                      <button
                        key={i}
                        onClick={() => setCurrentPage(i + 1)}
                        className={`px-3 py-1 rounded-md border ${
                          currentPage === i + 1
                            ? "bg-gray-700 text-white"
                            : "bg-white text-gray-700 border-gray-300"
                        }`}
                      >
                        {i + 1}
                      </button>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() =>
                        setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                      }
                      disabled={currentPage === totalPages}
                      className="px-3 py-1 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-100 disabled:opacity-50"
                    >
                      Next
                    </button>
                    <button
                      onClick={() => setCurrentPage(totalPages)}
                      disabled={currentPage === totalPages}
                      className="px-3 py-1 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-100 disabled:opacity-50"
                    >
                      Last
                    </button>
                  </div>
                </div>
              </div>
            </main>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Users;
